import classNames from 'classnames'
import * as React from 'react'
import {memo, useCallback, useState} from 'react'
import type {RenderedRows} from 'react-virtualized/dist/es/List'
import type List from 'react-virtualized/dist/es/List'

import {useAppSelector} from '../../../../../../hooks/react-redux'
import useEventCallback from '../../../../../../hooks/useEventCallback'
import {noop} from '../../../../../../utils/empty'
import type {TreeEvent} from '../../../../../common/Sidebar/Sidebar.types'
import {TreeEventType} from '../../../../../common/Sidebar/Sidebar.types'
import {keyboardToTreeEvent} from '../../../../../common/Sidebar/Sidebar.utils'
import VirtualizedTree from '../../../../../common/VirtualizedTree/VirtualizedTree'
import type {VirtualizedTreeChildProps} from '../../../../../common/VirtualizedTree/VirtualizedTree'
import {useAgentPools} from '../../../AgentPagePoolsProvider'
import type {
  AgentsTreeItemPool,
  AgentsTreeItemTitle,
  AgentsTreeItemType,
} from '../../../AgentsSidebar/AgentsSidebar.types'
import {AgentTreeItemCategory, AgentTreeItemType} from '../../../AgentsSidebar/AgentsSidebar.types'
import {INDEX_ATTRIBUTE} from '../../../AgentsSidebar/AgentsSidebarItem/AgentsSidebarItem'
import Search from '../../../components/Search/Search'
import {useToggleAgentPoolFavorite} from '../../hooks/useAgentPoolFavorite'
import {getAgentPoolsFavoriteDialogItems} from '../AgentPoolsFavoriteDialog.selectors'

import AgentPoolsFavoriteDialogItem from './AgentPoolsFavoriteItem'
import {findItemWithin} from './AgentPoolsFavoriteList.utils'

import styles from './AgentPoolsFavoriteList.css'

const ITEM_HEIGHT = 24

type Props = {
  favoritePools: number[]
  searchQuery?: string
}

function AgentPoolsFavoriteList(props: Props) {
  const {favoritePools} = props
  const [searchQuery, setSearchQuery] = useState(props.searchQuery ?? '')
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const listRef = React.useRef<List>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const [indexes, setIndexes] = React.useState({startIndex: -1, stopIndex: -1})

  const onRowsRendered = React.useCallback(({startIndex, stopIndex}: RenderedRows) => {
    setIndexes({startIndex, stopIndex})
  }, [])

  const pools = useAgentPools()
  const items = useAppSelector(state =>
    getAgentPoolsFavoriteDialogItems(state, {
      pools,
      favoritePools,
      searchQuery,
    }),
  )

  const calculateItemHeight = React.useCallback(
    (item: AgentsTreeItemType) => item.height ?? ITEM_HEIGHT,
    [],
  )

  const renderItem = React.useCallback(
    ({
      item,
      index,
      style,
      height,
      width,
      indent,
      parent,
      cache,
    }: VirtualizedTreeChildProps<AgentsTreeItemTitle | AgentsTreeItemPool>) => {
      const rest = {
        searchQuery,
        index,
        style,
        height,
        width,
        indent,
        parent,
        cache,
        setExpandState: noop,
        itemKey: item.key,
        onBlur: noop,
      }

      return <AgentPoolsFavoriteDialogItem {...rest} {...item} key={item.key} />
    },
    [searchQuery],
  )

  const focusItem = (itemIndex: number): void => {
    if (itemIndex < 0) {
      return
    }

    listRef.current?.scrollToRow(itemIndex)
    requestAnimationFrame(() => findItemWithin(itemIndex, wrapperRef.current)?.focus())
  }

  const toggle = useToggleAgentPoolFavorite()

  const onTreeEvent = React.useCallback(
    (treeEvent: TreeEvent) => {
      if (treeEvent.type === TreeEventType.EVENT_TYPE_FOCUS) {
        if (treeEvent.index < 0) {
          searchInputRef.current?.focus()
        } else if (treeEvent.index < items.length) {
          focusItem(treeEvent.index)
        }

        return
      }

      const item = items[treeEvent.index]

      if (
        treeEvent.type === TreeEventType.EVENT_TYPE_SELECT &&
        item?.type === AgentTreeItemType.POOL &&
        item?.id != null
      ) {
        toggle(item.id)

        if (item.category === AgentTreeItemCategory.FAVORITE) {
          focusItem(treeEvent.index)
        }
      }
    },
    [items, toggle],
  )

  const onSearchKeyDown = useEventCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === 'PageDown') {
      const treeEvent = keyboardToTreeEvent(
        e,
        items,
        indexes.startIndex,
        indexes.startIndex,
        indexes.stopIndex,
      )

      if (treeEvent != null) {
        onTreeEvent(treeEvent)
      }
    }
  })

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value),
    [],
  )
  const onSearchClear = useCallback(() => setSearchQuery(''), [])

  const onItemKeyDown = useEventCallback((e: React.KeyboardEvent) => {
    // Character, shift is OK
    if (e.key === 'Backspace' || (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey)) {
      searchInputRef.current?.focus()
      return
    }

    const focusedIndex = parseInt((e.target as Element).getAttribute(INDEX_ATTRIBUTE)!, 10)
    const treeEvent = keyboardToTreeEvent(
      e,
      items,
      focusedIndex,
      indexes.startIndex,
      indexes.stopIndex,
    )

    if (treeEvent != null) {
      onTreeEvent(treeEvent)
    }
  })

  return (
    <>
      <Search
        className={styles.search}
        forwardedRef={searchInputRef}
        onChange={onSearchChange}
        onClear={onSearchClear}
        onKeyDown={onSearchKeyDown}
        value={searchQuery}
        autoFocus
        placeholder={'Search for agent pools'}
      />
      <div className={styles.wrapper} ref={wrapperRef}>
        {items.length > 0 ? (
          <VirtualizedTree
            ariaLabel={'Agents'}
            className={styles.tree}
            flatItems={items}
            itemHeight={calculateItemHeight}
            firstLevelIndent={6}
            levelIndent={18}
            onKeyDown={onItemKeyDown}
            ref={listRef}
            onRowsRendered={onRowsRendered}
            tabIndex={-1}
          >
            {renderItem}
          </VirtualizedTree>
        ) : (
          <div className={classNames(styles.nothingFound, styles.tree)}>{'Nothing found'}</div>
        )}
      </div>
    </>
  )
}

export default memo(AgentPoolsFavoriteList)
